import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { LegacyOp, Op } from "./ops";

export type StoredBlob = { buf: ArrayBuffer; type: string; name?: string };
export type OutboxRecord = {
  seq?: number;
  /** Records written by older app versions may still hold a LegacyOp. */
  op: Op | LegacyOp;
  createdAt: number;
  attempts: number;
  state: "pending" | "failed";
  lastError?: string;
  /** The account that queued it. Missing on records queued before accounts: the next sign-in adopts them. */
  userId?: string;
};

interface Schema extends DBSchema {
  outbox: { key: number; value: OutboxRecord };
  blobs: { key: string; value: StoredBlob };
  kv: { key: string; value: unknown };
}

let dbp: Promise<IDBPDatabase<Schema>> | null = null;

export function db() {
  dbp ??= openDB<Schema>("peakstamps", 1, {
    upgrade(d) {
      d.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
      d.createObjectStore("blobs");
      d.createObjectStore("kv");
    },
    blocking() {
      // A newer SW/page wants to upgrade: let it.
      void dbp?.then((d) => d.close());
      dbp = null;
    },
  });
  return dbp;
}

export class QuotaError extends Error {
  constructor() {
    super("quota");
  }
}

export function isQuotaError(e: unknown): boolean {
  const name = (e as { name?: string })?.name;
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" || (e instanceof QuotaError);
}

/** Wrap IndexedDB writes so quota problems surface as a single, user-explainable error. */
export async function guardQuota<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (isQuotaError(e) || (e as Error)?.message?.includes("quota")) throw new QuotaError();
    throw e;
  }
}

export const kv = {
  get: async <T>(key: string) => (await (await db()).get("kv", key)) as T | undefined,
  set: async (key: string, value: unknown) => guardQuota(async () => void (await (await db()).put("kv", value, key))),
  del: async (key: string) => void (await (await db()).delete("kv", key)),
};

export async function putBlob(key: string, data: Blob | StoredBlob, name?: string) {
  // Store bytes (not Blob) — most robust across WebKit versions.
  const value: StoredBlob = data instanceof Blob ? { buf: await data.arrayBuffer(), type: data.type, name } : data;
  await guardQuota(async () => void (await (await db()).put("blobs", value, key)));
}

export async function getStoredBlob(key: string): Promise<StoredBlob | null> {
  return (await (await db()).get("blobs", key)) ?? null;
}

export async function getBlob(key: string): Promise<Blob | null> {
  const v = await (await db()).get("blobs", key);
  return v ? new Blob([v.buf], { type: v.type }) : null;
}

export async function deleteBlobs(keys: string[]) {
  const d = await db();
  const tx = d.transaction("blobs", "readwrite");
  await Promise.all([...keys.map((k) => tx.store.delete(k)), tx.done]);
}
